/**
 * Pure state machine behind the hotkey recorder, ported from the desktop behaviour:
 * - keys are collected while held; the PEAK set (every key held at once) is kept
 * - the combo is committed only when ALL keys are released
 * - Escape alone cancels; a combo of modifiers only is ignored
 */

export const MODIFIER_TOKENS: readonly string[] = ["Ctrl", "Alt", "Shift", "Meta"];

export interface RecorderState {
  readonly held: readonly string[];
  readonly peak: readonly string[];
}

export const RECORDER_IDLE: RecorderState = { held: [], peak: [] };

export type RecorderEvent = { readonly type: "down" | "up"; readonly token: string };

export type RecorderOutcome =
  | { readonly kind: "pending"; readonly state: RecorderState }
  | { readonly kind: "cancel" }
  | { readonly kind: "ignore" }
  | { readonly kind: "commit"; readonly tokens: readonly string[] };

function unique(tokens: readonly string[]): string[] {
  return [...new Set(tokens)];
}

function resolveRelease(peak: readonly string[]): RecorderOutcome {
  if (peak.length === 1 && peak[0] === "Escape") return { kind: "cancel" };
  if (peak.every((token) => MODIFIER_TOKENS.includes(token))) return { kind: "ignore" };
  return { kind: "commit", tokens: peak };
}

/** Advances the recorder by one key event. Pure: the input state is never mutated. */
export function reduceRecorder(state: RecorderState, event: RecorderEvent): RecorderOutcome {
  if (event.type === "down") {
    return {
      kind: "pending",
      state: {
        held: unique([...state.held, event.token]),
        peak: unique([...state.peak, event.token]),
      },
    };
  }
  if (!state.held.includes(event.token)) {
    return { kind: "pending", state };
  }
  const held = state.held.filter((token) => token !== event.token);
  if (held.length > 0) {
    return { kind: "pending", state: { held, peak: state.peak } };
  }
  return resolveRelease(state.peak);
}

/** Maps a keyboard event to a combo token. Codes are used for letters and digits so Shift does not change them. */
export function tokenFromKey(event: {
  readonly key: string;
  readonly code: string;
}): string | null {
  const modifier = MODIFIER_NAMES[event.key];
  if (modifier) return modifier;
  const letter = /^Key([A-Z])$/.exec(event.code);
  if (letter?.[1]) return letter[1];
  const digit = /^Digit([0-9])$/.exec(event.code);
  if (digit?.[1]) return digit[1];
  if (/^F([1-9]|1[0-2])$/.test(event.key)) return event.key;
  if (event.code === "Space" || event.key === " ") return "Space";
  if (event.key === "Escape") return "Escape";
  if (event.key.length === 1) return event.key.toUpperCase();
  return event.key.length > 0 ? event.key : null;
}

const MODIFIER_NAMES: Readonly<Record<string, string>> = {
  Control: "Ctrl",
  Shift: "Shift",
  Alt: "Alt",
  Meta: "Meta",
};
